import React, { useContext, useEffect, useState } from "react";
import { useNavigate, Link as RouterLink, data } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  Box, Button, FormControl, FormLabel, Input,
  FormErrorMessage, Select, VStack, Stack, Text, Link, Flex, Avatar,
  InputGroup, InputRightElement, IconButton
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import UserService from "../services/UserService";
import CommonService from "../services/CommonService";
import { extractCity } from "../utils/helper";
import SessionRestorePopup from "../components/SessionRestorePopup";
// Validation Schema
const schema = Yup.object().shape({
  MobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
  password: Yup.string()
    .min(4, "Password must be at least 4 characters")
    .required("Password is required"),
  role: Yup.string()
    .required("Role is required"),
  CompCd: Yup.string()
    .required("Company is required"),
});

const Login = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("");
  const [CompCd, setCompCd] = useState(0);
  const [otp, setOTP] = useState("");
  const [otpErrors, setotpErrors] = useState("");
  const [showBlock, setShowBlock] = useState(false);
  const [locationAllowed, setLocationAllowed] = useState(false);
  const [error, setErrors] = useState("")

  const [location, setLocation] = useState({
    latitude: parseFloat(0),
    longitude: parseFloat(0),
    error: null,
  });

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState([])

  const [showPassword, setShowPassword] = useState(false); // New state for password visibility

  const handleShowPasswordClick = () => setShowPassword(!showPassword);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const companyList = async () => {
    setCountry([])
    try {
      const res = await CommonService.getCompany();
      if (res.statusCode === 200) {
        setCountry(res.data)
      }
    } catch (error) {
      if (error.response?.status === 401) {
        alert(error.response.data.message)
      } else {

        alert(`Something went wrong. Please try again`)
      }
    }
  }

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const latitude = pos.coords.latitude;
          const longitude = pos.coords.longitude;
          setLocation({
            latitude: latitude,
            longitude: longitude,
            error: null,
          });

          getFullAddress(latitude, longitude);
          // console.log(pos.coords.latitude)
          // map on
        },
        (error) => {
          setLocationAllowed(false);
          setLocation({ ...location, error: 'Please enable location to continue' });
          setErrors('Please enable location to continue.');
        }
      );
    } else {
      setLocationAllowed(false);
      setLocation({ ...location, error: 'Geolocation is not supported by your browser' });
      setErrors('Geolocation is not supported by this browser.');
    }

    companyList()
  }, []);

  const handleCompany = (e) => {
    setLocationAllowed(false);
    setCompCd(Number(e.target.value));
  }

  useEffect(() => {
    setLocationAllowed(false);
    // Company selection and geocoding can finish in either order.
    if (!city) return;

    setErrors("");
    if (CompCd <= 0) return;

    let cancelled = false;
    const checkGeofence = async () => {
      try {
        const res = await CommonService.getGeofence({ CompCd });
        if (cancelled) return;

        if (res?.statusCode !== 200 || !Array.isArray(res.data)) {
          setErrors("Unable to check service availability. Please select your company again.");
          return;
        }

        const match = res.data.some(ct =>
          ct.city_name?.trim().toLowerCase() === city.trim().toLowerCase()
        );
        setLocationAllowed(match);
        setErrors(match ? "" : `The App is currently not available in ${city}`);
      } catch (error) {
        if (cancelled) return;
        setErrors("Unable to check service availability. Please select your company again.");
        console.error(error);
      }
    };

    checkGeofence();
    return () => { cancelled = true; };
  }, [CompCd, city]);

  const onSubmit = async (data) => {
    if (!locationAllowed) return;
    setMobile(data.MobileNo)
    setPassword(data.password)
    setRole(data.role)
    setCompCd(data.CompCd)
    try {
      const res = await login(data.MobileNo, data.password, data.role, data.CompCd);

      if (res.role > 0) {
        setShowBlock(true)
      }
    } catch (error) {
      if (error.response?.status === 401) {
        alert(error.response.data.message);
      } else {
        alert("Something went wrong. Please try again.");
      }
    }

  };

  const handleOTP = async (e) => {
    e.preventDefault();
    setotpErrors(null)
    if (otp) {
      let params = {
        MobileNo: mobile, Password: password, otp: otp, role: role, CompCd: CompCd
      };

      try {
        const res = await UserService.verifyLoginOTP(params);
        const user = JSON.parse(localStorage.getItem("user"));

        user.PassengerId = 0;
        user.ReqNo = 0;
        user.DriveNo = 0;
        user.DriverId = 0;
        user.address = address;
        user.city = city;

        localStorage.setItem("user", JSON.stringify(user));
        saveLocation();

        if (res.role === 5) {
          navigate("/admin");
        } else if (res.role === 1) {
          navigate("/driver/map");
        } else if (res.role === 2) {
          navigate("/user/map");
        } else {
          alert("Invalid credentials");
        }

      } catch (error) {
        if (error.response?.status === 401) {
          alert(error.response.data.message);
          // setotpErrors(error.response.data.message)
        } else {
          alert("Something went wrong. Please try again.");
        }
      }
    }
  };

  const resendOTP = async () => {
    if (mobile) {
      let params = {
        MobileNo: mobile
      };

      try {
        const res = await UserService.sendOTP(params);
        if (res.statusCode === 200) {
          alert(res.message);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          alert(error.response.data.message);
        } else {
          alert("Something went wrong. Please try again.");
        }
      }
    }
  }

  const getFullAddress = async (lat, lng) => {
    try {
      const response = await CommonService.getAddressFromLatLng(lat, lng);

      const data = response?.data; // ApiResponse.data

      if (data?.status !== "OK") {
        throw new Error(`Geocoding error: ${data?.status || "missing response"}`);
      }

      const result = data.results?.[0];

      if (!result) {
        throw new Error("No geocoding result found");
      }

      // Full formatted address
      const faddress = result.formatted_address;
      setAddress(faddress);

      // Extract city safely
      const ct = extractCity(result.address_components);

      console.log("Detected city:", ct);

      if (ct) {
        setCity(ct);

        return ct;
      } else {
        setLocationAllowed(false);
        setErrors("Unable to determine your city. Please reload and try again.");
        return false;
      }
    } catch (error) {
      setLocationAllowed(false);
      setErrors("Unable to determine your city. Please reload and try again.");
      console.error('Geocoding error:', error);
      return null;
    }
  };

  const checkFakeIp = async (lat, lng) => {
    let params = {
      lat: lat,
      lng: lng
    }
    try {
      const res = await CommonService.locationVerify(params);
      if (res.statusCode === 200) {
        if (res.data.distance > 0) {
          if (res.data.distance <= 2) {
            setLocationAllowed(true);
          } else {
            setLocationAllowed(false);
            setErrors(`Your GPS is not working properly. Please reboot & recheck`)
          }
        } else {
          setLocationAllowed(false);
          setErrors(`Your GPS is not working properly. Please reboot & recheck`)
        }
        //getFullAddress(res.data.ipLocation.lat, res.data.ipLocation.lng)

      }
    } catch (error) {
      if (error.response?.status === 401) {
        alert(error.response.data.message);
      } else {
        alert("Something went wrong. Please try again.");
        console.error(error);
      }
    }
  }

  const saveLocation = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    let params = {
      uId: Number(user.uId),
      lat: location.latitude,
      lng: location.longitude,
      location: address,
      city: city,
    };

    try {
      if (location.latitude) {
        await CommonService.saveStartLatLng(params);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }

  return (

    <Flex minH="100vh" align="center" justify="center" bg="gray.50">
      <Box
        bg="white"
        p={6}
        rounded="md"
        shadow="2xl"
        w="full"
        maxW="sm"
        mx={{ base: 4, md: 0 }}
        border="1px"
        borderColor="gray.200"
        textAlign="center"
        position="relative"
      >

        <Flex justify="center" mb={8}>
          <Avatar
            size="xl"
            src="/assets/images/aajaalogo.png" // Replace with actual image path
            boxSize="120px" // Custom size
            border="4px solid white"
            bg="#e7dddd"
            boxShadow="md"
            position="absolute"
            top="-60px"
            left="50%"
            transform="translateX(-50%)"
          />
        </Flex>

        {showBlock ? (
          <Box>
            <form onSubmit={handleOTP}>
              <VStack spacing={4}>
                <FormControl >
                  <FormLabel fontSize="sm">OTP</FormLabel>
                  <Input
                    type="number"
                    placeholder="OTP"
                    size='sm'
                    maxLength={4}
                    value={otp} // value is set here
                    onChange={(e) => setOTP(e.target.value)} // updates state
                  />
                  {otpErrors && (<FormErrorMessage>{otpErrors}</FormErrorMessage>)}
                </FormControl>

                <Stack spacing={4} direction='row' align='center'>
                  <Button
                    colorScheme="red"
                    type="submit"
                    fontSize="sm"
                    size='sm'
                  >
                    Verify OTP
                  </Button>

                  <Button
                    colorScheme="green"
                    type="button"
                    fontSize='sm'
                    size='sm'
                    onClick={resendOTP}
                  >
                    Resend OTP
                  </Button>
                </Stack>

              </VStack>
            </form>
          </Box>
        ) : (

          <>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack spacing={2}>

                <FormControl isInvalid={!!errors.CompCd}>
                  <FormLabel fontSize="sm">Company</FormLabel>
                  <Select size="sm" {...register("CompCd", { onChange: handleCompany })}>
                    <option value="">Select</option>
                    {country.map((row, i) => (
                      <option value={row.CompCd} key={row.CompCd}>{row.Name}</option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.CompCd?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.MobileNo}>
                  <FormLabel fontSize="sm">Mobile</FormLabel>
                  <Input
                    type="number"
                    placeholder="Mobile"
                    inputMode="numeric"
                    {...register("MobileNo")}
                    size='sm'
                  />
                  <FormErrorMessage>{errors.MobileNo?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.password}>
                  <FormLabel fontSize="sm">Password</FormLabel>
                  <InputGroup size='sm'> {/* Wrap Input with InputGroup */}
                    <Input
                      type={showPassword ? "text" : "password"} // Dynamic type based on state
                      placeholder="Password"
                      {...register("password")}
                    />
                    <InputRightElement width='2.5rem'> {/* For the icon */}
                      <IconButton
                        h='1.75rem'
                        size='sm'
                        onClick={handleShowPasswordClick}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        variant="ghost" // Make it blend in
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.role}>
                  <FormLabel fontSize="sm">Role</FormLabel>
                  <Select size="sm" {...register("role")}>
                    <option value=''>Select</option>
                    <option value={1}>Driver</option>
                    <option value={2}>Passenger</option>
                  </Select>
                  <FormErrorMessage>{errors.role?.message}</FormErrorMessage>
                </FormControl>

                <Button
                  colorScheme="teal"
                  type="submit"
                  width="full"
                  fontSize={{ base: "sm", md: "md" }}
                  size='sm'
                  isDisabled={!locationAllowed || !!error}
                >
                  Login
                </Button>
              </VStack>
            </form>
            {error && (
              <Box textAlign="center" fontSize='sm'>
                <Text fontSize="sm" color="red.500">
                  {error}
                </Text>
              </Box>
            )}
            <Box mt={4} textAlign="center" fontSize={{ base: "sm", md: "md" }}>
              <Text fontSize="sm" mt={4}>
                Don't have an account?{' '}
                <Link as={RouterLink} to="/signup" color="teal.500" fontWeight="semibold">
                  Sign Up
                </Link>
              </Text>
              <Text fontSize="sm">
                <Link as={RouterLink} to="/forgot-password" color="blue.500" fontWeight="semibold">
                  Forgot your password?
                </Link>
              </Text>
            </Box>

          </>

        )}
        <SessionRestorePopup />
      </Box>
    </Flex >
  );
};

export default Login;
